import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getToken } from './authApi';

/**
 * Notification Types
 */
export enum NotificationType {
  MESSAGE = 'message',              // Thông báo tin nhắn
  TRACKING = 'tracking',            // Tracking Steps - Cập nhật bước theo dõi
  NEW_PROPERTY = 'new_property',    // Đăng sản phẩm mới
  SCHEDULE = 'schedule',            // Thông báo lịch đặt thuê
  PAYMENT = 'payment',              // Thông báo lịch thanh toán
  CONTRACT = 'contract',            // Cập nhật hợp đồng
  VERIFICATION = 'verification',    // Xác minh KYC
  COMMISSION = 'commission',        // Thông báo hoa hồng/thu nhập
  SYSTEM = 'system',                // Thông báo hệ thống
  APPROVAL = 'approval',            // Thông báo phê duyệt
  ALERT = 'alert',                  // Cảnh báo
}

export enum NotificationStatus {
  UNREAD = 'unread',
  READ = 'read',
  ARCHIVED = 'archived',
}

/**
 * Notification Interface
 */
export interface Notification {
  id: number;
  user_id: number;
  notification_type: NotificationType | string;
  title: string;
  message: string;
  content?: Record<string, any>;
  metadata?: {
    related_id?: number;
    property_id?: number;
    contract_id?: number;
    order_id?: number;
    message_id?: number;
    action_url?: string;
    image_url?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    [key: string]: any;
  };
  status: NotificationStatus | string;
  read_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationCreateRequest {
  notification_type: NotificationType | string;
  title: string;
  message: string;
  metadata?: Record<string, any>;
}

export interface NotificationResponse {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  status: string;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
}

/**
 * Notification API Service
 */
export const notificationApi = {
  // Get all notifications for current user
  getNotifications: async (
    limit: number = 50,
    offset: number = 0,
    status?: NotificationStatus
  ): Promise<NotificationListResponse> => {
    try {
      const token = getToken();
      const params: any = { limit, offset };
      
      if (status) {
        params.status = status;
      }
      
      const response = await axios.get(`${API_BASE_URL}/user/notifications`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }) as any;

      return response.data || {
        notifications: [],
        total: 0,
        unread_count: 0
      };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Return empty response on error instead of throwing
      return {
        notifications: [],
        total: 0,
        unread_count: 0
      };
    }
  },

  // Get unread notification count
  getUnreadCount: async (): Promise<{ unread_count: number }> => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/user/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return (response.data as { unread_count: number }) || { unread_count: 0 };
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return { unread_count: 0 };
    }
  },

  // Get notification by ID
  getNotification: async (notificationId: number): Promise<Notification> => {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/user/notifications/${notificationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return (response.data as Notification);
  },

  // Mark notification as read
  markAsRead: async (notificationId: number): Promise<Notification> => {
    try {
      const token = getToken();
      const response = await axios.put(
        `${API_BASE_URL}/user/notifications/${notificationId}/read`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return (response.data as Notification) || null;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark multiple notifications as read
  markMultipleAsRead: async (notificationIds: number[]) => {
    try {
      const token = getToken();
      const response = await axios.put(
        `${API_BASE_URL}/user/notifications/read-batch`,
        { notification_ids: notificationIds },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return (response.data as { updated: number }) || { updated: 0 };
    } catch (error) {
      console.error('Error marking multiple notifications as read:', error);
      return { updated: 0 };
    }
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<{ updated: number }> => {
    try {
      const token = getToken();
      const response = await axios.put(
        `${API_BASE_URL}/user/notifications/read-all`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return (response.data as { updated: number }) || { updated: 0 };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return { updated: 0 };
    }
  },

  // Delete notification
  deleteNotification: async (notificationId: number): Promise<{ deleted: boolean }> => {
    try {
      const token = getToken();
      const response = await axios.delete(
        `${API_BASE_URL}/user/notifications/${notificationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return (response.data as { deleted: boolean }) || { deleted: true };
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { deleted: false };
    }
  },

  // Get notifications by type
  getNotificationsByType: async (
    type: NotificationType | string,
    limit: number = 50,
    offset: number = 0
  ): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/user/notifications/type/${type}`, {
      params: { limit, offset },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return (response.data as Notification[]) || [];
  },

  // Get notifications for specific property
  getPropertyNotifications: async (
    propertyId: number,
    limit: number = 20
  ): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/property/${propertyId}`,
      {
        params: { limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },

  // Get contract notifications
  getContractNotifications: async (contractId: number): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/contract/${contractId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },

  // Get message notifications (tin nhắn)
  getMessageNotifications: async (
    limit: number = 20,
    offset: number = 0
  ): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/type/message`,
      {
        params: { limit, offset },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },

  // Get tracking notifications (các bước theo dõi)
  getTrackingNotifications: async (
    propertyId?: number,
    limit: number = 20
  ): Promise<Notification[]> => {
    const token = getToken();
    const params: any = { limit };
    if (propertyId) params.property_id = propertyId;
    
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/type/tracking`,
      {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },

  // Get payment notifications (lịch thanh toán)
  getPaymentNotifications: async (
    limit: number = 20
  ): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/type/payment`,
      {
        params: { limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },

  // Get schedule notifications (lịch đặt thuê)
  getScheduleNotifications: async (
    limit: number = 20
  ): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/type/schedule`,
      {
        params: { limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },

  // Get new property notifications
  getNewPropertyNotifications: async (
    limit: number = 20
  ): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/type/new_property`,
      {
        params: { limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },

  // Get commission notifications (thu nhập)
  getCommissionNotifications: async (
    limit: number = 20
  ): Promise<Notification[]> => {
    const token = getToken();
    const response = await axios.get(
      `${API_BASE_URL}/user/notifications/type/commission`,
      {
        params: { limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return (response.data as Notification[]);
  },
};
